

import React, { useContext, useState } from 'react';

import { IdentityContext } from '../../context';
import { AddressContext } from '../../pages';
import {
  Address, BirthInfo, Person
} from '../../types';
import { Button } from '../basic-inputs';

import '../../../styles/profile.scss';
import { ScreenHeader } from '../v2';

interface ProfilePhotoProps {
  photoUrl?: string;
  firstName?: string;
  lastName?: string;
}

export function ProfileComponent() {
  const { person } = useContext(IdentityContext);
  const { address } = useContext(AddressContext);
  const [ activeTab, setActiveTab ] = useState('name');

  // Extract birth information from profile
  const birthInfo: BirthInfo = {
    dob: person.dob,
    birthCountry: person.birthCountry,
    birthState: person.birthState,
    birthCounty: person.birthCounty,
    birthCity: person.birthCity
  };

  // Handle edit actions
  const handleEditName = () => {
    console.log('Edit name clicked');
    // Implement edit functionality
  };

  const handleEditAddress = () => {
    console.log('Edit address clicked');
    // Implement edit functionality
  };

  const handleEditBirth = () => {
    console.log('Edit birth info clicked');
    // Implement edit functionality
  };

  const handleEditRace = () => {
    console.log('Edit race clicked');
    // Implement edit functionality
  };

  return (
    <div className="profile">
      <ScreenHeader title="Profile Information" description="" />
      
      <div className="profile-content">
        <ProfilePhoto
          photoUrl={person.photoUrl}
          firstName={person.firstName}
          lastName={person.lastName}
        />

        <div className="profile-tabs">
          <div className="tab-navigation">
            <button
              className={`tab-button ${activeTab === 'name' ? 'active' : ''}`}
              onClick={() => setActiveTab('name')}
            >
              Name
            </button>
            <button
              className={`tab-button ${activeTab === 'address' ? 'active' : ''}`}
              onClick={() => setActiveTab('address')}
            >
              Address
            </button>
            <button
              className={`tab-button ${activeTab === 'birth' ? 'active' : ''}`}
              onClick={() => setActiveTab('birth')}
            >
              Birth
            </button>
            <button
              className={`tab-button ${activeTab === 'race' ? 'active' : ''}`}
              onClick={() => setActiveTab('race')}
            >
              Race
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'name' && <NameTab profile={person} onEdit={handleEditName} />}

            {activeTab === 'address' && <AddressTab address={address} onEdit={handleEditAddress} />}

            {activeTab === 'birth' && <BirthTab birthInfo={birthInfo} onEdit={handleEditBirth} />}

            {activeTab === 'race' && <RaceTab race={person.race} onEdit={handleEditRace} />}
          </div>
        </div>
      </div>
    </div>
  );
}

export const ProfilePhoto: React.FC<ProfilePhotoProps> = ({ photoUrl, firstName, lastName }) => {
  // Create initials from name if photo not available
  const initials = firstName && lastName ? `${firstName[0]}${lastName[0]}` : '?';

  return (
    <div className="profile-photo-container">
      {photoUrl ? (
        <div className="profile-photo">
          <img src={photoUrl} alt={`${firstName} ${lastName}`} />
        </div>
      ) : (
        <div className="profile-photo-placeholder">
          <span>{initials}</span>
        </div>
      )}
    </div>
  );
};

// NameTab Component
interface NameTabProps {
  profile: Person;
  onEdit?: () => void;
}

const NameTab: React.FC<NameTabProps> = ({ profile, onEdit }) => {
  return (
    <div className="profile-tab-content">
      <div className="form-group">
        <label>FIRST NAME</label>
        <div className="form-control">{profile.firstName}</div>
      </div>

      <div className="form-group">
        <label>MIDDLE NAME</label>
        <div className="form-control">{profile.middleName || 'Not provided'}</div>
      </div>

      <div className="form-group">
        <label>LAST NAME</label>
        <div className="form-control">{profile.lastName}</div>
      </div>

      <div className="form-group">
        <label>MAIDEN NAME</label>
        <div className="form-control">{profile.maidenName || 'Not provided'}</div>
      </div>

      <Button className="edit-button" onClick={onEdit}>
        Edit
      </Button>
    </div>
  );
};

// AddressTab Component
interface AddressTabProps {
  address?: Address;
  onEdit?: () => void;
}

const AddressTab: React.FC<AddressTabProps> = ({ address, onEdit }) => {
  if (!address) {
    return (
      <div className="profile-tab-content">
        <p className="no-data-message">No address information provided.</p>
        <button className="edit-button" onClick={onEdit}>
          Add Address
        </button>
      </div>
    );
  }

  return (
    <div className="profile-tab-content">
      <div className="form-group">
        <label>STREET ADDRESS</label>
        <div className="form-control">{address.address1}</div>
      </div>

      {address.address2 && (
        <div className="form-group">
          <label>APARTMENT/UNIT</label>
          <div className="form-control">{address.address2}</div>
        </div>
      )}

      <div className="form-group">
        <label>CITY</label>
        <div className="form-control">{address.city}</div>
      </div>

      <div className="form-row">
        <div className="form-group half">
          <label>STATE</label>
          <div className="form-control">{address.state}</div>
        </div>

        <div className="form-group half">
          <label>ZIP CODE</label>
          <div className="form-control">{address.zip}</div>
        </div>
      </div>

      <div className="form-group">
        <label>COUNTRY</label>
        <div className="form-control">{address.country}</div>
      </div>

      <Button className="edit-button" onClick={onEdit}>
        Edit
      </Button>
    </div>
  );
};

// BirthTab Component
interface BirthTabProps {
  birthInfo: BirthInfo;
  onEdit?: () => void;
}

const BirthTab: React.FC<BirthTabProps> = ({ birthInfo, onEdit }) => {
  // Format the date of birth
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not provided';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="profile-tab-content">
      <div className="form-group">
        <label>DATE OF BIRTH</label>
        <div className="form-control">{formatDate(birthInfo.dob)}</div>
      </div>

      <div className="form-group">
        <label>BIRTH CITY</label>
        <div className="form-control">{birthInfo.birthCity || 'Not provided'}</div>
      </div>

      <div className="form-group">
        <label>BIRTH COUNTY</label>
        <div className="form-control">{birthInfo.birthCounty || 'Not provided'}</div>
      </div>

      <div className="form-group">
        <label>BIRTH STATE</label>
        <div className="form-control">{birthInfo.birthState || 'Not provided'}</div>
      </div>

      <div className="form-group">
        <label>BIRTH COUNTRY</label>
        <div className="form-control">{birthInfo.birthCountry || 'Not provided'}</div>
      </div>

      <Button className="edit-button" onClick={onEdit}>
        Edit
      </Button>
    </div>
  );
};

// RaceTab Component
interface RaceTabProps {
  race?: string;
  onEdit?: () => void;
}

const RaceTab: React.FC<RaceTabProps> = ({ race, onEdit }) => {
  return (
    <div className="profile-tab-content">
      <div className="form-group">
        <label>RACE</label>
        <div className="form-control">{race || 'Not provided'}</div>
      </div>

      <Button className="edit-button" onClick={onEdit}>
        Edit
      </Button>
    </div>
  );
};
